import { FC, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  DateTimeTemplate,
  DropdownBoundTemplate,
  DropdownTemplate,
  GroupTitleTemplate,
  MultiselectBoundTemplate,
  MultiselectTemplate,
  SwitchTemplate,
} from "@ryde/controls";
import {
  AssignDocksDuringProductionEnum,
  BulkProduceTypeEnum,
  CUSTOM_PRODUCTION_PRODUCE_JOIN_TYPES,
  CUSTOM_PRODUCTION_RIDE_TYPES,
  CustomProductionFormFields,
  DirectionEnum,
  DIRECTIONS_INCLUDE_BOTH,
  DirectionsServiceEnum,
  filterProductionTypeSelectItems,
  getDefaultProductionTypes,
  getNextDayEndDateTime,
  getNextDayStartDateTime,
  ProductionTypeEnum,
  PROVIDER_OPTIONS,
  selectListBookingsTypes,
  showProduceButtonBookTypeSystemParamSelector,
  systemSettingsDtoAtom,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import { CustomProductionFormStyled } from "./custom-production-form.styled";
import { customProductionValidationRules } from "./custom-production.validation";

export const CustomProductionFormTemplate: FC = () => {
  const { dateFormat, timeFormat } = useFormats();
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const { timezone } = useTimezone();
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const isHubsLogicEnabled = Boolean(
    systemSettingsDto?.metadata?.enableHubsLogic,
  );
  const isDocksEnabled = Boolean(
    systemSettingsDto?.metadata?.allowDockStations,
  );
  const docksSelectItems = useMemo(
    () => [
      {
        value: AssignDocksDuringProductionEnum.NoDocks,
        label: t(`selectListLabel.${AssignDocksDuringProductionEnum.NoDocks}`),
      },
      {
        value: AssignDocksDuringProductionEnum.DropoffOnly,
        label: t(
          `selectListLabel.${AssignDocksDuringProductionEnum.DropoffOnly}`,
        ),
      },
    ],
    [t],
  );
  const { defaultRideTypeForProduction } = systemSettingsDto?.metadata ?? {};
  const showProduceButtonBookTypeSystemParam = useRecoilValue(
    showProduceButtonBookTypeSystemParamSelector,
  );
  const productionAction = getValues(
    CustomProductionFormFields.productionAction,
  );

  useEffect(() => {
    if (defaultRideTypeForProduction) {
      setValue(
        CustomProductionFormFields.rideTypeId,
        defaultRideTypeForProduction,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }
  }, [defaultRideTypeForProduction, setValue]);

  useEffect(() => {
    const defaultDirection = DIRECTIONS_INCLUDE_BOTH?.find(
      option => option.value === DirectionEnum.Both,
    );

    if (defaultDirection) {
      setValue(CustomProductionFormFields.direction, defaultDirection.value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [setValue]);

  useEffect(() => {
    const currentTypes = getValues(
      CustomProductionFormFields.productionType,
    ) as ProductionTypeEnum[] | undefined;
    if (currentTypes?.length) {
      if (
        !isHubsLogicEnabled &&
        currentTypes.includes(ProductionTypeEnum.Hub)
      ) {
        setValue(
          CustomProductionFormFields.productionType,
          currentTypes.filter(type => type !== ProductionTypeEnum.Hub),
          { shouldDirty: true, shouldValidate: true },
        );
      }
      return;
    }
    setValue(
      CustomProductionFormFields.productionType,
      getDefaultProductionTypes(isHubsLogicEnabled),
      { shouldDirty: false, shouldValidate: true },
    );
  }, [getValues, isHubsLogicEnabled, setValue]);

  const productionTypeItems = useMemo(
    () =>
      filterProductionTypeSelectItems(
        CUSTOM_PRODUCTION_RIDE_TYPES,
        isHubsLogicEnabled,
      ),
    [isHubsLogicEnabled],
  );
  return (
    <CustomProductionFormStyled>
      <GroupTitleTemplate
        name="configurations_title"
        formLabel={t("produceButton.formLabel.configurations_title")}
      />

      <DropdownTemplate
        name={CustomProductionFormFields.productionAction}
        formLabel={t("produceButton.formLabel.productionAction")}
        formValidation={customProductionValidationRules.productionAction}
        formAttributes={{
          selectItems: CUSTOM_PRODUCTION_PRODUCE_JOIN_TYPES,
        }}
      />

      {(productionAction === BulkProduceTypeEnum.Produce ||
        productionAction === BulkProduceTypeEnum.Both) && (
        <MultiselectTemplate
          name={CustomProductionFormFields.productionType}
          formLabel={t("produceButton.formLabel.productionType")}
          formValidation={customProductionValidationRules.productionType}
          formAttributes={{
            selectItems: productionTypeItems,
          }}
          formVisibleCondition={{
            productionAction: { $in: ["produce", "both"] },
          }}
        />
      )}

      <DateTimeTemplate
        name={CustomProductionFormFields.produceFrom}
        formLabel={t("produceButton.formLabel.produceFrom")}
        formValidation={customProductionValidationRules.produceFrom}
        timeFormat={timeFormat}
        dateFormat={dateFormat}
        defaultValue={getNextDayStartDateTime(timezone)}
      />
      <DateTimeTemplate
        name={CustomProductionFormFields.produceTo}
        formLabel={t("produceButton.formLabel.produceTo")}
        formValidation={customProductionValidationRules.produceTo}
        timeFormat={timeFormat}
        dateFormat={dateFormat}
        defaultValue={getNextDayEndDateTime(timezone)}
      />

      <DropdownTemplate
        name={CustomProductionFormFields.direction}
        formLabel={t("produceButton.formLabel.direction")}
        formValidation={customProductionValidationRules.direction}
        formAttributes={{
          selectItems: DIRECTIONS_INCLUDE_BOTH,
        }}
      />

      <MultiselectBoundTemplate
        name={CustomProductionFormFields.shiftIds}
        formLabel={t("produceButton.formLabel.shiftIds")}
        formValidation={customProductionValidationRules.shiftIds}
        formAttributes={{
          dropItemsSource: "shift",
        }}
      />

      <MultiselectBoundTemplate
        name={CustomProductionFormFields.areaIds}
        formLabel={t("produceButton.formLabel.areaIds")}
        formValidation={customProductionValidationRules.areaIds}
        formAttributes={{
          dropItemsSource: "area",
        }}
      />
      <MultiselectBoundTemplate
        name={CustomProductionFormFields.siteIds}
        formLabel={t("produceButton.formLabel.siteIds")}
        formValidation={customProductionValidationRules.siteIds}
        formAttributes={{
          dropItemsSource: "site",
        }}
      />
      <MultiselectBoundTemplate
        name={CustomProductionFormFields.unitIds}
        formLabel={t("produceButton.formLabel.unitIds")}
        formValidation={customProductionValidationRules.unitIds}
        formAttributes={{
          dropItemsSource: "unit",
        }}
      />
      <DropdownBoundTemplate
        name={CustomProductionFormFields.passengerTypeId}
        formLabel={t("produceButton.formLabel.passengerTypeId")}
        formValidation={customProductionValidationRules.passengerTypeId}
        formAttributes={{
          dropItemsSource: "passengerType",
        }}
      />

      <MultiselectBoundTemplate
        name={CustomProductionFormFields.sectors}
        formLabel={t("produceButton.formLabel.sectors")}
        formValidation={customProductionValidationRules.sectors}
        formAttributes={{
          dropItemsSource: "passengerSector",
          skipDropdownItemsTranslation: true,
        }}
      />

      {showProduceButtonBookTypeSystemParam && (
        <DropdownTemplate
          name={CustomProductionFormFields.bookingType}
          formLabel={t("produceButton.formLabel.bookingType")}
          formValidation={customProductionValidationRules.bookingType}
          formAttributes={{
            selectItems: selectListBookingsTypes,
          }}
        />
      )}

      {isDocksEnabled && (
        <DropdownTemplate
          name={CustomProductionFormFields.assignDocksDuringProduction}
          formLabel={t("produceButton.formLabel.assignDocksDuringProduction")}
          formValidation={
            customProductionValidationRules.assignDocksDuringProduction
          }
          formAttributes={{
            selectItems: docksSelectItems,
            skipDropdownItemsTranslation: true,
            tooltipTranslationKey:
              "produceButton.tooltip.assignDocksDuringProduction",
          }}
        />
      )}

      <GroupTitleTemplate
        name="ride_setting_title"
        formLabel={t("produceButton.formLabel.ride_setting_title")}
      />

      <DropdownBoundTemplate
        name={CustomProductionFormFields.rideTypeId}
        formLabel={t("produceButton.formLabel.rideTypeId")}
        formAttributes={{
          dropItemsSource: "rideType",
        }}
      />

      <DropdownTemplate
        name={CustomProductionFormFields.directionsService}
        formLabel={t("produceButton.formLabel.directionsService")}
        formValidation={customProductionValidationRules.directionsService}
        formAttributes={{
          selectItems: PROVIDER_OPTIONS.filter(
            option => option.value !== DirectionsServiceEnum.TomTom,
          ),
        }}
      />

      <SwitchTemplate
        name={CustomProductionFormFields.doNotLockBookings}
        formLabel={t("produceButton.formLabel.doNotLockBookings")}
        formValidation={customProductionValidationRules.doNotLockBookings}
        formAttributes={{
          tooltipTranslationKey: "produceButton.tooltip.doNotLockBookings",
        }}
      />
    </CustomProductionFormStyled>
  );
};
